import React, { useState, useEffect } from 'react';
import {MessageSquare, Check, X, MessagesSquare  } from "lucide-react";
import { API_URL, doApiGet, doApiMethod } from "../services/apiService";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import {convertToHebrewDate} from "../utils/HebrewDateConverter";

const ForumMessages = ({ selectedFamily, setSubmitStatus }) => {
    const [error, setError] = useState(null);
    const [forumMessages, setForumMessages] = useState({ approved: [], pending: [] });
    const [activeTab, setActiveTab] = useState('pending');
    const [statusMessage, setStatusMessage] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, messageId: null });


    useEffect(() => {
        fetchForumMessages();
    }, [selectedFamily]);

    const fetchForumMessages = async () => {
        try {
            const [approvedData, unapprovedData] = await Promise.all([
                doApiGet(`${API_URL}/messagesBazach/approved/${selectedFamily}`),
                doApiGet(`${API_URL}/messagesBazach/unapproved/${selectedFamily}`)
            ]);

            const approvedMessages = approvedData.messages || [];
            const pendingMessages = Array.isArray(unapprovedData) ? unapprovedData : [];

            setForumMessages({
                approved: approvedMessages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
                pending: pendingMessages.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            });
        } catch (err) {
            setError('שגיאה בטעינת ההודעות: ' + err.message);
        }
    };

    const handleMessageAction = async (messageId, action) => {
        try {
            if (action === 'approve') {
                await doApiMethod(`${API_URL}/messagesBazach/${messageId}/approve/${selectedFamily}`, "PATCH");
            } else if (action === 'delete') {
                await doApiMethod(`${API_URL}/messagesBazach/${messageId}/${selectedFamily}`, "DELETE");
            }
            await fetchForumMessages();
        } catch (err) {
            console.error('Error handling message action:', err);
        }
    };

    const handleDelete = async () => {
        if (!deleteDialog.messageId) return;

        try {
            await doApiMethod(
                `${API_URL}/messagesBazach/${deleteDialog.messageId}`,
                "DELETE"
            );

            await fetchForumMessages();
            setStatusMessage({ type: 'success', message: 'ההודעה נמחקה בהצלחה' });

            setTimeout(() => {
                setStatusMessage(null);
            }, 3000);
        } catch (err) {
            console.error('Error deleting message:', err);
            setStatusMessage({ type: 'error', message: 'שגיאה במחיקת ההודעה' });

            setTimeout(() => {
                setStatusMessage(null);
            }, 3000);
        } finally {
            setDeleteDialog({ isOpen: false, messageId: null });
        }
    };

    const MessageCard = ({ message, type }) => {
        if (!message || typeof message !== 'object') return null;

        const { name = '', content = '', createdAt = '', _id = '', email = '' } = message;
        const formattedDate = convertToHebrewDate(createdAt);

        return (
            <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(201,166,108,0.08)] mb-4">
                <div className="flex justify-between items-start gap-4">
                    <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#937c5d] to-[#c9a66c]
                        flex items-center justify-center text-white font-heebo font-bold">
                                {name.charAt(0) || '?'}
                            </div>
                            <div>
                                <h4 className="font-heebo text-[#4a3f30] font-bold">{name}</h4>
                                {email && (
                                    <div className="text-sm text-[#937c5d]/80 mb-1">{email}</div>
                                )}
                                <time className="text-sm text-[#937c5d]/70">{formattedDate}</time>
                            </div>
                        </div>
                        <p className="text-[#4a3f30] text-lg font-heebo leading-relaxed">{content}</p>
                    </div>
                    <div className="flex gap-2">
                        {type === 'pending' && (
                            <button
                                onClick={() => handleMessageAction(_id, 'approve')}
                                className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-colors"
                                title="אשר הודעה"
                            >
                                <Check size={20}/>
                            </button>
                        )}
                        <button
                            onClick={() => setDeleteDialog({isOpen: true, messageId: _id})}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                            title="מחק הודעה"
                        >
                            <X size={20}/>
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl p-6 shadow-[0_4px_20px_rgba(201,166,108,0.08)]">
            <h2 className="text-xl font-heebo font-bold text-[#937c5d] mb-6 flex items-center gap-2
                border-b border-[#e6dfd4] pb-4">
            <MessagesSquare   className="w-6 h-6"/>
                ניהול הודעות פורום
            </h2>
            {/* Tabs */}
            <div className="flex gap-4 mb-6 border-b border-[#e6dfd4]" dir="rtl">
                <button
                    onClick={() => setActiveTab('pending')}
                    className={`pb-4 px-4 relative transition-all ${
                        activeTab === 'pending'
                            ? 'text-[#937c5d] font-bold after:content-[""] after:absolute after:bottom-0 after:right-0 after:w-full after:h-0.5 after:bg-[#c9a66c] after:rounded-full'
                            : 'text-[#937c5d]/60 hover:text-[#937c5d]'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <MessageSquare className="w-5 h-5"/>
                        הודעות ממתינות לאישור ({forumMessages.pending.length})
                    </div>
                </button>
                <button
                    onClick={() => setActiveTab('approved')}
                    className={`pb-4 px-4 relative transition-all ${
                        activeTab === 'approved'
                            ? 'text-[#937c5d] font-bold after:content-[""] after:absolute after:bottom-0 after:right-0 after:w-full after:h-0.5 after:bg-[#c9a66c] after:rounded-full'
                            : 'text-[#937c5d]/60 hover:text-[#937c5d]'
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <Check className="w-5 h-5"/>
                        הודעות מאושרות ({forumMessages.approved.length})
                    </div>
                </button>
            </div>
            {statusMessage && (
                <div
                    className={`mb-4 p-4 rounded-lg font-heebo text-center transition-all duration-300 ${
                        statusMessage.type === 'success'
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                    }`}
                >
                    {statusMessage.message}
                </div>
            )}

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                isOpen={deleteDialog.isOpen}
                onClose={() => setDeleteDialog({ isOpen: false, messageId: null })}
                onConfirm={handleDelete}
            />
            {/* Messages Content */}
            <div className="space-y-4">
                {activeTab === 'pending' && (
                    <>
                        {forumMessages.pending.map(message => (
                            <MessageCard key={message._id} message={message} type="pending"/>
                        ))}
                        {forumMessages.pending.length === 0 && (
                            <p className="text-[#937c5d] text-center py-4">אין הודעות ממתינות לאישור</p>
                        )}
                    </>
                )}

                {activeTab === 'approved' && (
                    <>
                        {forumMessages.approved.map(message => (
                            <MessageCard key={message._id} message={message} type="approved"/>
                        ))}
                        {forumMessages.approved.length === 0 && (
                            <p className="text-[#937c5d] text-center py-4">אין הודעות מאושרות</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ForumMessages;